import React, { isValidElement, useMemo, type ReactNode } from "react";
import { NavItemProps } from "../types";
import { Composer } from "components/src/components/Composer/Composer";
import { ComposerElementType } from "../../../../Composer/types";
import { Icon } from "components/src/components/Icon/Icon";
import { Badge } from "components/src/components/Badge/Badge";
import { isValidIcon } from "shared/src/hooks/useImageResolver";
import { iconPresets } from "../js/stackedNavPresets";
import { NavItemStackedSkeleton } from "./_NavItemStackedSkeleton";
import cx from "components/src/utils/cx";
import { cssColor } from "shared/src/utils/cssUtils";

function buildIconType(icon, props, Component) {
  if (!icon) return null;
  if (isValidElement(icon)) return icon;
  if (typeof icon === "string") return <Component name={icon} {...props} />;
  if (typeof icon === "object") return <Component {...props} {...icon} />;
  return null;
}

export const sizeMap = {
  xs: 28,
  sm: 32,
  md: 36,
  lg: 42,
};

const badgeOffsetMap = {
  lg: {
    x: "6px",
    y: "-5px",
  },
  md: {
    x: "4px",
    y: "-4px",
  },
  sm: {
    x: "5px",
    y: "-4px",
  },
  xs: {
    x: "6px",
    y: "-4px",
  },
};

function buildComposerProps({
  leadingIcon,
  badge,
  overlay,
  trailingIcon,
  isExternalLink,
  showBadgeDropShadow = true,
  size,
  mode,
  id
}) {
  const [hasLeadingIcon, hasTrailingIcon, hasBadge, hasOverlay] = [
    (typeof leadingIcon === "string" && isValidIcon(leadingIcon)) || typeof leadingIcon === "object" && "name" in leadingIcon ||
      isValidElement(leadingIcon),
    (typeof trailingIcon === "string" && isValidIcon(trailingIcon)) ||
      isValidElement(trailingIcon),
    typeof badge !== "undefined" &&
      (typeof badge === "object" || isValidElement(badge)),
    (typeof overlay !== "undefined" && overlay) || isValidElement(overlay),
  ];

  const iconName = typeof leadingIcon === "string" ? leadingIcon : typeof leadingIcon === "object" ? leadingIcon?.name : "";

  const isLeadingPreset = hasLeadingIcon && iconName in iconPresets;
  const isTrailingPreset = hasTrailingIcon && trailingIcon in iconPresets;
  const iconPreset = isLeadingPreset ? iconPresets[iconName](size) : null;

  const trailingIconPreset = isTrailingPreset ? iconPresets[trailingIcon](size) : null;

  const boostBadgeProps = { bgColor: "status-notification", fgColor: "fg-inverse" };
 
  const processedIcons: Record<string, ReactNode | string | null> = {
    leadingIcon: buildIconType(
      leadingIcon,
      isLeadingPreset && iconPreset
        ? iconPreset
        : { size: sizeMap[size], color: leadingIcon?.color ?? "fg-default", mode, elementTiming: `featured_nav` },
      Icon
    ),
    trailingIcon: buildIconType(
      hasBadge ? badge : hasTrailingIcon ? trailingIcon : null,
      hasBadge ? { size: "xs" , showDropShadow: showBadgeDropShadow, ...boostBadgeProps } : isTrailingPreset ? trailingIconPreset : { size: sizeMap[size] / 3, color: "fg-moderate", elementTiming: `nav-${id}-leading-image` },
      hasBadge ? Badge : Icon
    ),
    overlay: buildIconType(
      overlay,
      { size: sizeMap[size] / 2, color: "fg-default", mode, elementTiming: `featured_nav` },
      Icon
    ),
  };

  const fg: ComposerElementType = {
    bg: null,
    fg: {
      element: processedIcons.leadingIcon,
      positioning: {
        position: "absolute",
        justify: "center",
        width: sizeMap[size],
        height: sizeMap[size],
      },
    },
  };
  const trailing: ComposerElementType = {
    bg: null,
    fg: {
      positioning: {
        position: "absolute",
        justify: "topRight",
        justifyOffsetY: badgeOffsetMap[size].y,
        justifyOffsetX: badgeOffsetMap[size].x
      },
      element: isExternalLink ? <div className="cui-p-2px cui-bg-bg-default cui-grid cui-place-content-center cui-rounded-1" style={{ height: sizeMap[size] / 3 }}>{processedIcons.trailingIcon}</div> : processedIcons.trailingIcon
    }
  };
  const bg: ComposerElementType = {
    bg: { primaryColor: "var(--neutrals-transparent)", position: "absolute" },
  };
  const _overlay: ComposerElementType = {
    bg: null,
    fg: {
      element: processedIcons.overlay,
      positioning: { position: "absolute", justify: "bottomRight" },
    },
  };

  const elements: ComposerElementType[] = [bg, fg, trailing, _overlay];

  return { elements };
}

export const NavItemStacked = ({
  id,
  label,
  leadingIcon,
  badge,
  overlay,
  size,
  trailingIcon,
  isExternalLink,
  skeleton,
  boldText,
  textColor = "fg-default", 
  bgColor = "bg-default",
  showBadgeDropShadow = true,
  mode = "light",
  type,
  onClick,
  ...rest
}: NavItemProps) => {
  const _size = typeof size === "string" && size in sizeMap ? size : "md";
  const _trailingIcon = isExternalLink ? "icon_ui_launch" : trailingIcon;
 
  const composerProps = useMemo(
    () =>
      buildComposerProps({
        leadingIcon,
        badge,
        trailingIcon: _trailingIcon,
        overlay,
        isExternalLink,
        showBadgeDropShadow,
        mode,
        size: _size,
        id
      }),
    [leadingIcon, badge, trailingIcon, isExternalLink, overlay, size, boldText, showBadgeDropShadow, mode, id]
  );
  const defaultClasses = useMemo(() =>{
    return "cui-border-1 cui-border-solid cui-border-fg-subtle active:cui-border-fg-primary focus-visible:cui-border-fg-focus focus-visible:cui-outline-none"
  },[type]);

  const _style = useMemo(()=>{
    return {
      color: cssColor(textColor)
    }
  },[textColor])

  if (skeleton) return <NavItemStackedSkeleton size={_size} />

  return (
    <div
      className={
        cx({
            "cui-px-2xs cui-py-2px":  size === "xs",
            "cui-py-2px cui-px-2xs": size !== "xs",
            [defaultClasses]: type !== "minimal"
        },"cui__nav-item--stacked cui-flex cui-flex-col cui-items-center cui-h-auto cui-cursor-pointer cui-select-none hover:cui-bg-bg-layer-one active:cui-bg-bg-layer-two cui-rounded-2 cui-gap-2xs")
      }
      onClick={onClick}
      key={`nav-item-stacked-${id}`}
      tabIndex={0}
      style={{
        backgroundColor: bgColor ? cssColor(bgColor) : "var(--bg-default)"
      }}
      data-qa={rest?.dataQa}
    >
      <Composer
        {...composerProps}
        positioning={{ width: "100%", height: sizeMap[_size] }}
      />
      <div className={`heading-${size}${boldText ? "-bold" : ""} cui-min-w-[32px] cui-max-w-[90px] cui-truncate cui-text-center`}
      style={_style}>
        {label}
      </div>
    </div>
  );
};
